import { AppStore, CurrentUserStore, PitstopCookie } from 'stores';

/**
 * Centralized authentication token manager
 * Handles token refresh, validation, and automatic logout
 */
class AuthTokenManager {
  constructor() {
    this.isRefreshing = false;
    this.failedQueue = [];
    this.maxRetries = 3;
    this.retryCount = 0;
  }

  /**
   * Process the failed queue after token refresh
   */
  processQueue(error, token = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });

    this.failedQueue = [];
  }

  /**
   * Get access token with automatic refresh if needed
   */
  async getValidAccessToken() {
    let accessToken;

    if (AppStore.isOnGeotabDashboard()) {
      accessToken = await PitstopCookie.get('accessToken');
    } else {
      accessToken = PitstopCookie.get('accessToken');
    }

    if (!accessToken) {
      return null;
    }

    return accessToken;
  }

  /**
   * Handle token refresh when 401 is received
   */
  async handleTokenRefresh() {
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      let refreshToken;

      if (AppStore.isOnGeotabDashboard()) {
        refreshToken = await PitstopCookie.get('refreshToken');
      } else {
        refreshToken = PitstopCookie.get('refreshToken');
      }

      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      // Attempt to refresh the token
      const newUser =
        await CurrentUserStore.loginWithRefreshToken(refreshToken);

      if (!newUser) {
        throw new Error('Token refresh failed');
      }

      // Get the new access token
      let newAccessToken;
      if (AppStore.isOnGeotabDashboard()) {
        newAccessToken = await PitstopCookie.get('accessToken');
      } else {
        newAccessToken = PitstopCookie.get('accessToken');
      }

      this.processQueue(null, newAccessToken);
      this.retryCount = 0;

      return newAccessToken;
    } catch (error) {
      this.retryCount++;

      console.error('Token refresh failed:', error);
      this.processQueue(error, null);

      if (
        this.retryCount >= this.maxRetries ||
        error.message.includes('refresh')
      ) {
        this.forceLogout('Your session has expired. Please log in again.');
        throw new Error('Session expired');
      }

      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Force user logout and redirect to login page
   */
  forceLogout(message = 'Your session has expired. Please log in again.') {
    console.log('Forcing logout due to auth failure');

    AppStore.addError(message);

    this.retryCount = 0;

    setTimeout(() => {
      CurrentUserStore.logout();
    }, 2000);
  }

  /**
   * Check if current path requires authentication
   */
  requiresAuth(pathname = window.location.pathname) {
    const noAuthPaths = [
      '/',
      '/login',
      '/login/geotab',
      '/register',
      '/reset-password',
      '/callback',
      '/callbackEntra',
    ];

    return !noAuthPaths.includes(pathname) && !pathname.includes('code=');
  }

  /**
   * Validate current authentication state
   */
  async validateAuthState() {
    if (!this.requiresAuth()) {
      return true;
    }

    const accessToken = await this.getValidAccessToken();

    if (!accessToken) {
      this.forceLogout('Please log in to continue.');
      return false;
    }

    return true;
  }
}

const authTokenManager = new AuthTokenManager();
export default authTokenManager;
